<script>
    import Sparkline from "./Sparkline.svelte";
    import TinyMap from "./TinyMap.svelte";

    export let data = []; // habeas cases [{ court_id, state, date_filed, ... }]
    export let mapping = []; // [{ state_abbreviation: "ca", "state_name": "California", ... }]
    export let agg_data = []; // aggregate data by court [{"court_id": "akd","count": 4,"state": "ak"}, {...}]
    export let courts_mapping = []; // [{ court_id: "akd", "court_name": "District of Alaska", ... }] Note for future refactoring, there might be a way to combine the two mapping JSONs
    export let guff;
    /** @type { [string, string][] } */
    export let court_id_and_map_url_pairs; // [ ["flsd", "assets/flsd-1234.svg"], ... ]

    const spreadsheetIcon = `<svg aria-label="Download our data" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-download"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>`;


    $: court_id_to_map_url = new Map(court_id_and_map_url_pairs);

    // Takes out Guam, Virgin Islands, Northern Mariana Islands - we can put them back in if cases start to pop off in these places
    const EXCLUDED_TERRITORIES = new Set(["gu", "vi", "mp"]);

    // --- Helper functions ---
    const mapAbrevToName = new Map(
        mapping
            .filter((o) => !EXCLUDED_TERRITORIES.has(o.state_abbreviation))
            .map((o) => [o.state_abbreviation, o.state_name]),
    ); // maps state abbreviations to state names

    const stateNameToAbbrev = new Map(
        mapping
            .filter((o) => !EXCLUDED_TERRITORIES.has(o.state_abbreviation))
            .map((o) => [o.state_name, o.state_abbreviation]),
    ); // maps state namess to state abbreviations

    // Removed Guam, Virgin Islands, Northern Mariana Islands from both the data and the courts_mapping data
    $: filteredData = data.filter((d) => !EXCLUDED_TERRITORIES.has(d.state));

    // Filter out excluded territories from agg_data
    $: filteredAggData = agg_data.filter(
        (d) => !EXCLUDED_TERRITORIES.has(d.state),
    );

    $: filteredCourts_mapping = courts_mapping.filter((court) => {
        const stateAbbrev = stateNameToAbbrev.get(court.state);
        return stateAbbrev && !EXCLUDED_TERRITORIES.has(stateAbbrev);
    });

    // --- Grouping functions ---
    // Created new lookup maps from agg_data for state and district counts
    $: courtCountMap = new Map(
        filteredAggData.map((d) => [d.court_id, d.count]),
    );

    $: stateCountMap = filteredAggData.reduce((acc, d) => {
        acc.set(d.state, (acc.get(d.state) || 0) + d.count);
        return acc;
    }, new Map());

    // Created new lookup maps from agg_data for state and district month to date, year to date
    $: ytd_courtCountMap = new Map(
        filteredAggData.map((d) => [d.court_id, d.YTD]),
    );

    $: ytd_stateCountMap = filteredAggData.reduce((acc, d) => {
        acc.set(d.state, (acc.get(d.state) || 0) + d.YTD);
        return acc;
    }, new Map());

    $: mtd_courtCountMap = new Map(
        filteredAggData.map((d) => [d.court_id, d.MTD]),
    );

    $: mtd_stateCountMap = filteredAggData.reduce((acc, d) => {
        acc.set(d.state, (acc.get(d.state) || 0) + d.MTD);
        return acc;
    }, new Map());

    // Group data by court and state (for sparklines only!)
    $: habeasByCourt = filteredData.reduce((acc, d) => {
        if (!acc[d.court_id]) acc[d.court_id] = [];
        acc[d.court_id].push(d);
        return acc;
    }, {}); // groups all data by court district (court_id)

    $: habeasByState = filteredData.reduce((acc, d) => {
        if (!acc[d.state]) acc[d.state] = [];
        acc[d.state].push(d);
        return acc;
    }, {}); // groups all data by state (state)

    // Loops over the courts_mapping JSON which connects court_id and creates an object with counts of total cases by court districts
    // along with the data needed
    $: courtsByState = filteredCourts_mapping.reduce((acc, court) => {
        const stateName = court.state;
        const stateAbbrev = stateNameToAbbrev.get(stateName);

        // if there's no court data still create an object for zero cases

        if (!acc[stateName]) {
            acc[stateName] = {
                state_name: stateName,
                state_abbreviation: stateAbbrev,
                count: stateCountMap.get(stateAbbrev) || 0,
                mtd: mtd_stateCountMap.get(stateAbbrev) || 0,
                ytd: ytd_stateCountMap.get(stateAbbrev) || 0,
                sparklineData: habeasByState[stateAbbrev] || [],
                courts: [],
            };
        }
        const courtCount = courtCountMap.get(court.court_id) || 0; // Finds the count using agg_data
        const courtCases = habeasByCourt[court.court_id] || [];

        acc[stateName].courts.push({
            court_id: court.court_id,
            court_name: court.court_name,
            count: courtCount, // Finds the count using agg_data
            mtd: mtd_courtCountMap.get(court.court_id) || 0,
            ytd: ytd_courtCountMap.get(court.court_id) || 0,
            sparklineData: courtCases,
        });

        return acc;
    }, {});

    // Map to states but also sort the cases by court and sort the cases by state descending
    $: states = Object.values(courtsByState)
        .map((state) => ({
            ...state,
            courts: [...state.courts].sort((a, b) => b.count - a.count),
        }))
        .sort((a, b) => b.count - a.count);

    // Text search functions
    let textSearch = "";

    $: filteredStates = states.filter((state) =>
        state.state_name.toLowerCase().includes(textSearch.toLowerCase()),
    );
</script>

<div id="state-table" class="newsapp" style="max-width:40em;margin: var(--spacing3) auto">
    <div class="flex-box">
        <input
            type="search"
            placeholder="Search state"
            name="input-state"
            bind:value={textSearch}
            class="state_search"
        />
        <div class="since-2025-download">
            <a
                role="button"
                class="download"
                aria-label={guff["download_hed"]}
                aria-labelledby={guff["download_hed"]}
                href="https://propublica.s3.us-east-1.amazonaws.com/projects/habeas-tracker/latest/{guff['update_date']}/propublica_habeas_{guff['update_date']}_all_cases_since_Jan2025.csv"
                download
                >{guff["download_hed"]}
                <div class="icon">
                    {@html spreadsheetIcon}
                </div>
            </a>
        </div>
    </div>
    <table class="table">
        <thead>
            <tr>
                <th colspan="2">State</th>

                <th class="right">{guff["total_cases_hed"]}</th>
                <th class="right">{guff["trend_line"]}</th>
                <th class="right"></th>
            </tr>
        </thead>

        <tbody>
            <!-- Row for each state even the ones with zero cases -->
            {#each filteredStates as state}
                <tr class="state-row">
                    <td class="state_stateface"
                        ><span class="stateface sf-{state.state_abbreviation}"
                        ></span></td
                    >
                    <td class="state_name">
                        {state.state_name}

                    </td>

                    <td class="table__td--numeric">
                        {state.count.toLocaleString("en-US")}
                    </td>

                    <td class="table__td--numeric">
                        {#key state.state_name}
                            {#if state.sparklineData.length > 1}
                                <Sparkline
                                    data={state.sparklineData}
                                    type={"state"}
                                    update_date={guff["update_date"]}
                                />
                            {/if}
                        {/key}
                    </td>
                     <td class="table__td--string">
                       {#if state.sparklineData.length > 0}
                            <a
                                role="button"
                                class="download"
                                aria-label="Download this state's data"
                                aria-labelledby="Download this state's data"
                                href="https://propublica.s3.us-east-1.amazonaws.com/projects/habeas-tracker/latest/{guff['update_date']}/states/propublica_habeas_{guff['update_date']}_{state.state_abbreviation}.csv"
                                download
                            >
                                <div class="icon">
                                    {@html spreadsheetIcon}
                                </div>
                            </a>
                        {/if}
                     </td>
                </tr>
                <!-- Row for each court district if there is a district in that state -->
                {#if state.courts.length > 1}
                    {#each state.courts as court}
                        <tr class="district-row">
                            <td></td>
                            <td class="district-name">
                                <span class="tiny-map-wrapper">
                                    <TinyMap
                                        courtAbb={court.court_id}
                                        mapUrl={court_id_to_map_url.get(
                                            court.court_id,
                                        )}
                                    />
                                </span>
                                <span class="court_name"
                                    >{court.court_name}</span
                                >

                            </td>

                            <td class="table__td--numeric"
                                >{court.count.toLocaleString("en-US")}</td
                            >
                            <td class="table__td--numeric">
                                {#key court.court_id}
                                    {#if court.sparklineData.length > 1}
                                        <Sparkline
                                            data={court.sparklineData}
                                            type={"district"}
                                            update_date={guff["update_date"]}
                                        />
                                    {/if}
                                {/key}
                            </td>
                            <td class="table__td--string">
                              {#if court.sparklineData.length > 0}
                                    <a
                                        role="button"
                                        class="download"
                                        aria-label="Download this court district data"
                                        aria-labelledby="Download this court district data"
                                        href="https://propublica.s3.us-east-1.amazonaws.com/projects/habeas-tracker/latest/{guff['update_date']}/court_districts/propublica_habeas_{guff['update_date']}_{court.court_id}.csv"
                                        download
                                    >
                                        <div class="icon">
                                            {@html spreadsheetIcon}
                                        </div>
                                    </a>
                                {/if}
                            </td>
                        </tr>
                    {/each}
                {/if}
            {/each}
        </tbody>
    </table>
</div>

<style>
</style>
